import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { House } from 'phosphor-react';
import { bffClient, useSharedAddress } from '@sluitstraat/core';
import { commonStyles, addressSectionStyles as styles } from '../styles/commonStyles';

export interface Address {
  postcode: string;
  houseNumber: string;
  addition: string;
  street: string;
  city: string;
}

interface AddressSectionProps {
  value?: Partial<Address>;
  /** Required for editable mode; omit in readOnly (summary) mode. */
  onChange?: (address: Address) => void;
  onValidChange?: (valid: boolean) => void;
  shareAddress?: boolean;
  title?: string;
  required?: boolean;
  locked?: boolean;
  showErrors?: boolean;
  /** Section icon (a phosphor-react icon). Defaults to House; pass Users for a policyholder block. */
  icon?: React.ComponentType<any>;
  /**
   * Optional content rendered inside the card, between the header and the address fields
   * (e.g. a "risk address same as policyholder?" question). Shown in both editable and
   * readOnly modes.
   */
  topContent?: React.ReactNode;
  /**
   * Render a compact, non-editable summary of the address instead of the form.
   * Used when the address was already collected elsewhere and is only displayed
   * for reference. Distinct from `locked`, which keeps the full editable layout
   * but disables the inputs.
   */
  readOnly?: boolean;
  /** Input id prefix; distinct per section when two are on one page (label focus, a11y). */
  idPrefix?: string;
}

type AdditionOption = { value: string; label: string };
type FieldName = 'postcode' | 'houseNumber' | 'addition';

const formatPostcode = (v: string) => {
  const c = v.replace(/\s/g, '').toUpperCase();
  return c.length > 4 ? `${c.slice(0, 4)} ${c.slice(4)}` : c;
};
const unformatPostcode = (v: string) => v.replace(/\s/g, '').toUpperCase();
const isPostcodeValid = (v: string) => /^[0-9]{4}[A-Z]{2}$/.test(v);
const isHouseNumberValid = (v: string) => v === '' || /^[0-9]+$/.test(v);

const DEBOUNCE_MS = 500;

const parseAdditions = (res: any, pc: string, hn: string, st: string, ct: string): AdditionOption[] => {
  const codelist = res?.output?.Codelists || res?.output?.codelist;
  const item = Array.isArray(codelist) ? codelist[0] : null;
  const key = item ? Object.keys(item).find((k) => k.includes('TOEVOEG')) : null;
  const options: string[] = key && Array.isArray(item[key]) ? item[key].map((o: any) => o.const).filter(Boolean) : [];
  if (options.length === 0) return [];
  return [
    { value: '', label: `${st} ${hn}, ${formatPostcode(pc)} ${ct}` },
    ...options.map((a) => ({ value: a, label: `${st} ${hn} ${a}, ${formatPostcode(pc)} ${ct}` })),
  ];
};

const WarningIcon = (
  <svg style={commonStyles.additionSelectorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const CheckIcon = (
  <svg style={commonStyles.addressValidatedIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const AlertIcon = (
  <svg style={commonStyles.addressErrorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  error?: string | null;
  htmlFor?: string;
  children: React.ReactNode;
}> = ({ label, required, error, htmlFor, children }) => (
  <div style={styles.field}>
    <label style={commonStyles.formLabel} htmlFor={htmlFor}>
      {label}{required && <span style={commonStyles.required}> *</span>}
    </label>
    {children}
    {error && <p style={commonStyles.fieldErrorMessage}>{error}</p>}
  </div>
);

const Feedback: React.FC<{
  container: React.CSSProperties;
  textStyle: React.CSSProperties;
  icon: React.ReactNode;
  label: string;
}> = ({ container, textStyle, icon, label }) => (
  <div style={container}>
    {icon}
    <span style={textStyle}>{label}</span>
  </div>
);

const AdditionPicker: React.FC<{
  options: AdditionOption[];
  selected: string;
  onSelect: (value: string) => void;
}> = ({ options, selected, onSelect }) => {
  const { t } = useTranslation();
  return (
    <div style={styles.additionPicker}>
      <div style={commonStyles.additionSelectorHeader}>
        {WarningIcon}
        <span style={commonStyles.additionSelectorText}>
          {t('common.address.multipleFound', 'Meerdere adressen gevonden. Maak een keuze:')}
        </span>
      </div>
      <div style={commonStyles.addressOptionsContainer}>
        {options.map((o) => (
          <div
            key={o.value || '__none__'}
            onClick={() => onSelect(o.value)}
            style={commonStyles.addressOption}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary-color, #056666)';
              e.currentTarget.style.backgroundColor = '#f0f7ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.backgroundColor = '#fff';
            }}
          >
            <div style={commonStyles.addressOptionRadio}>
              <div
                style={{ ...commonStyles.addressOptionRadioInner, ...(selected === o.value ? styles.radioInnerOn : styles.radioInnerOff) }}
              />
            </div>
            <div style={commonStyles.addressOptionLabel}>{o.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AddressSection: React.FC<AddressSectionProps> = ({
  value,
  onChange,
  onValidChange,
  shareAddress = true,
  title = 'common.address.title',
  required = true,
  locked = false,
  showErrors = false,
  icon: Icon = House,
  readOnly = false,
  topContent,
  idPrefix = 'address',
}) => {
  const { t } = useTranslation();
  const { address: shared, updateAddress } = useSharedAddress();

  // The shared policyholder address only seeds a section that SHARES it; a standalone block
  // (shareAddress false, the insured-property address) starts from its own value alone.
  const seed = shareAddress ? shared : undefined;
  const [postcode, setPostcode] = useState(value?.postcode || seed?.BA_PCODE || '');
  const [houseNumber, setHouseNumber] = useState(value?.houseNumber || seed?.BA_HUISNR || '');
  const [addition, setAddition] = useState(value?.addition || seed?.BA_TOEVOEG || '');
  const [street, setStreet] = useState(value?.street || seed?.BA_STRAAT || '');
  const [city, setCity] = useState(value?.city || seed?.BA_PLAATS || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<'notFound' | 'failed' | null>(null);
  const [additions, setAdditions] = useState<AdditionOption[]>([]);
  const [revealed, setRevealed] = useState<Partial<Record<FieldName, boolean>>>({});

  const controller = useRef<AbortController | null>(null);
  const lastKey = useRef(street && city ? `${postcode}|${houseNumber}|${addition}` : '');
  const inFlightKey = useRef<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipInitialDebounce = useRef(true);
  const additionsResolvedFor = useRef('');

  const emit = (next: Partial<Address>) => {
    const addr: Address = { postcode, houseNumber, addition, street, city, ...next };
    onChange?.(addr);
    if (shareAddress) {
      updateAddress({
        BA_PCODE: addr.postcode,
        BA_HUISNR: addr.houseNumber,
        BA_TOEVOEG: addr.addition,
        BA_STRAAT: addr.street,
        BA_PLAATS: addr.city,
      });
    }
    onValidChange?.(Boolean(addr.street && addr.city));
  };

  const clearResolved = () => {
    if (street) setStreet('');
    if (city) setCity('');
    if (additions.length) setAdditions([]);
    if (error) setError(null);
    additionsResolvedFor.current = '';
  };

  const lookup = async (pc: string, hn: string, add: string, force = false) => {
    if (!isPostcodeValid(pc) || !hn) return;
    const key = `${pc}|${hn}|${add}`;
    if (key === lastKey.current && !force && street && city) return;
    // Dedupe: a request for this exact query is already in flight (e.g. the debounce
    // timer AND on-blur both fired for the same input). Don't send a second identical call.
    if (key === inFlightKey.current && !force) return;

    controller.current?.abort();
    const ctrl = new AbortController();
    controller.current = ctrl;
    inFlightKey.current = key;
    setLoading(true);
    setError(null);

    try {
      const res = await bffClient.addressAutocomplete('VP_PCODE', pc, hn, add || undefined, ctrl.signal);
      // Ignore responses from a superseded request — only the latest may touch state,
      // so a stale/aborted call can never overwrite a newer successful result with an error.
      if (controller.current !== ctrl) return;
      const vp = res?.output?.Contractdocument?.PP?.VP;
      const st = vp?.VP_STRAAT?.trim() || '';
      const ct = vp?.VP_PLAATS?.trim() || '';

      if (!st || !ct) {
        setStreet('');
        setCity('');
        setAdditions([]);
        setError('notFound');
        emit({ street: '', city: '' });
        return;
      }

      lastKey.current = key;
      setStreet(st);
      setCity(ct);
      const resolved = additionsResolvedFor.current === `${pc}|${hn}`;
      setAdditions(add || resolved ? [] : parseAdditions(res, pc, hn, st, ct));
      emit({ street: st, city: ct });
    } catch (e: any) {
      if (controller.current === ctrl && e?.name !== 'AbortError') setError('failed');
    } finally {
      if (controller.current === ctrl) {
        setLoading(false);
        inFlightKey.current = null;
      }
    }
  };

  const selectAddition = (a: string) => {
    additionsResolvedFor.current = `${postcode}|${houseNumber}`;
    setAddition(a);
    setAdditions([]);
    lookup(postcode, houseNumber, a, true);
  };

  const settle = () => {
    if (locked) return;
    setRevealed({ postcode: true, houseNumber: true });
    emit({});
    lookup(postcode, houseNumber, addition);
  };

  const settleNow = () => {
    if (debounce.current) clearTimeout(debounce.current);
    settle();
  };

  const disarm = (name: FieldName) => setRevealed((p) => (p[name] ? { ...p, [name]: false } : p));

  useEffect(() => {
    if (locked || readOnly || skipInitialDebounce.current) {
      skipInitialDebounce.current = false;
      return;
    }
    if (street && city && `${postcode}|${houseNumber}|${addition}` === lastKey.current) return;
    debounce.current = setTimeout(settle, DEBOUNCE_MS);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the debounce is keyed on the typed fields; settle/street/city are read at fire time
  }, [postcode, houseNumber, addition, locked]);

  const postcodeInvalid = Boolean(postcode) && !isPostcodeValid(postcode);
  const houseNumberInvalid = Boolean(houseNumber) && !isHouseNumberValid(houseNumber);
  const postcodeError = postcodeInvalid && (Boolean(revealed.postcode) || showErrors);
  const houseNumberError = houseNumberInvalid && (Boolean(revealed.houseNumber) || showErrors);
  const validated = Boolean(street && city) && !error && !loading;
  const showError = !loading && (error !== null || (showErrors && !(street && city)));

  const editableInput = (
    name: FieldName,
    id: string,
    val: string,
    onType: (v: string) => void,
    placeholder: string,
    hasError?: boolean,
    extra?: React.InputHTMLAttributes<HTMLInputElement>,
  ) => (
    <input
      id={id}
      type="text"
      value={val}
      placeholder={placeholder}
      readOnly={locked}
      onChange={locked ? undefined : (e) => { onType(e.target.value); clearResolved(); disarm(name); }}
      onBlur={locked ? undefined : settleNow}
      style={{
        ...commonStyles.fieldInput,
        ...(locked ? styles.inputLocked : {}),
        ...(hasError ? commonStyles.fieldInputError : {}),
      }}
      {...extra}
    />
  );

  if (readOnly) {
    const pc = value?.postcode || seed?.BA_PCODE || '';
    const hn = String(value?.houseNumber || seed?.BA_HUISNR || '');
    const ad = value?.addition || seed?.BA_TOEVOEG || '';
    const st = value?.street || seed?.BA_STRAAT || '';
    const ct = value?.city || seed?.BA_PLAATS || '';
    const summary = `${st} ${hn}${ad ? ` ${ad}` : ''}, ${formatPostcode(pc)} ${ct}`.trim();
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={commonStyles.sectionIconContainer}>
            <Icon size={28} weight="duotone" color="var(--secondary-color, #F51C17)" />
          </div>
          <h2 style={styles.title}>
            {t(title, 'Adresgegevens')}
          </h2>
        </div>
        {topContent}
        <input type="text" value={summary} readOnly style={commonStyles.fieldInputReadonly} />
        <Feedback
          container={commonStyles.addressValidatedIndicator}
          textStyle={commonStyles.addressValidatedText}
          icon={CheckIcon}
          label={t('common.address.validated', 'Adres gevonden')}
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>

      <div style={styles.header}>
        <div style={commonStyles.sectionIconContainer}>
          <Icon size={28} weight="duotone" color="var(--secondary-color, #F51C17)" />
        </div>
        <h2 style={styles.title}>
          {t(title, 'Adresgegevens')}
        </h2>
      </div>

      {topContent}

      <div className="sl-addr-fields-row">
        <Field
          htmlFor={`${idPrefix}-postcode`}
          label={t('common.address.postcode', 'Postcode')}
          required={required}
          error={postcodeError ? t('common.address.postcodeInvalid', 'Vul een geldige postcode in') : null}
        >
          {editableInput(
            'postcode',
            `${idPrefix}-postcode`,
            formatPostcode(postcode),
            (v) => setPostcode(unformatPostcode(v)),
            t('common.address.postcodePlaceholder', 'Postcode'),
            postcodeError,
            { maxLength: 7 },
          )}
        </Field>

        <Field
          htmlFor={`${idPrefix}-huisnr`}
          label={t('common.address.houseNumber', 'Huisnummer')}
          required={required}
          error={houseNumberError ? t('common.address.houseNumberInvalid', 'Alleen cijfers') : null}
        >
          {editableInput(
            'houseNumber',
            `${idPrefix}-huisnr`,
            houseNumber,
            setHouseNumber,
            t('common.address.houseNumberPlaceholder', 'Nr.'),
            houseNumberError,
            { inputMode: 'numeric' },
          )}
        </Field>

        <Field htmlFor={`${idPrefix}-toevoeging`} label={t('common.address.addition', 'Toevoeging')}>
          {editableInput(
            'addition',
            `${idPrefix}-toevoeging`,
            addition,
            setAddition,
            t('common.address.additionPlaceholder', 'Toev.'),
          )}
        </Field>
      </div>

      {!locked && additions.length > 0 && (
        <AdditionPicker options={additions} selected={addition} onSelect={selectAddition} />
      )}

      <div className="sl-addr-street-city-row" style={loading ? styles.streetCityRowMt8 : styles.streetCityRowMt10}>
        <Field label={t('common.address.street', 'Straat')} required={required}>
          <input
            type="text"
            value={street}
            readOnly
            placeholder={t('common.address.autofillPlaceholder', 'Wordt automatisch gevuld…')}
            style={commonStyles.fieldInputReadonly}
          />
        </Field>
        <Field label={t('common.address.city', 'Plaats')} required={required}>
          <input
            type="text"
            value={city}
            readOnly
            placeholder={t('common.address.autofillPlaceholder', 'Wordt automatisch gevuld…')}
            style={commonStyles.fieldInputReadonly}
          />
        </Field>
      </div>

      {loading && (
        <Feedback
          container={commonStyles.addressLookupLoading}
          textStyle={commonStyles.addressLookupText}
          icon={<span style={commonStyles.addressLookupSpinner} />}
          label={t('common.address.lookingUp', 'Adres opzoeken...')}
        />
      )}
      {validated && (
        <Feedback
          container={commonStyles.addressValidatedIndicator}
          textStyle={commonStyles.addressValidatedText}
          icon={CheckIcon}
          label={t('common.address.validated', 'Adres gevonden')}
        />
      )}
      {!validated && showError && (
        <Feedback
          container={commonStyles.addressErrorIndicator}
          textStyle={commonStyles.addressErrorText}
          icon={AlertIcon}
          label={error === 'failed'
            ? t('common.address.lookupFailed', 'Adres opzoeken mislukt')
            : t('common.address.notFound', 'Vul een geldig adres in')}
        />
      )}
    </div>
  );
};

export default AddressSection;
